// Catalog.ID Client — the on-demand key loader (device-setup-restructure §9.1)
// ============================================================================
// **Absent is not locked** (§1 item 12). A member may keep a key OFF this
// device (whitepaper §3.4.7, the manifest in `state/device-key-selection.ts`).
// When something then needs that key, the old answer — "keys not available" —
// was wrong twice over: it looked like a locked tab, and it left the member no
// way forward. This module is the way forward, and every consumer shares it:
// prompt for the supersecret, decrypt THAT ONE key's entries from the server
// bag, use them for the session, and offer to keep them here.
//
// ── the three rules this file exists to hold ────────────────────────────────
//
// 1. **A locked bag is not an absent key** (§12's trap). `getPrivateKey`
//    answers null for both, so a consumer that skipped the `isLoaded()` check
//    would meet a supersecret dialog where a PIN prompt was meant. This module
//    checks it itself and throws `cid-locked`, the shape the callers already
//    map to their `cid-locked` refusal.
//
// 2. **A mandatory key never reaches the prompt.** `main_signing_key`,
//    `attr_encrypt` and `derive_root` are on every device that stores keys at
//    all (§1 item 10), precisely because the calls that need them are
//    non-interactive data-plane calls that must never stop to ask. Asking for
//    one here is a programming error and throws as one, so no future caller
//    can route a derive or a blob read through a supersecret dialog.
//
// 3. **One prompt, one key, zeroed.** The derived key opens only the entries
//    of the requested id and is zeroed on every path, including the throwing
//    one (audit M12's posture, which `derive-root-store.ts` also keeps). The
//    blob plan removed every ROUTINE supersecret prompt; the only prompts this
//    adds are for a key the member explicitly declined on this device, which
//    is the consequence the picker's copy describes (§12).
//
// The import offer is not the same act as the load: the load is for THIS
// session, the import is a durable copy under the device encryption key. A
// COPY hand-over (`export-mail-encrypt`, `release`) passes `import: 'never'` —
// a module asking for the bytes of a key is no reason to start storing it here.

import type { ShellAPI } from '@catalog-shell';
import { createElement } from '@catalog-shell';
import * as accountApi from '../api/account';
import * as aes from '../crypto/aes';
import {
    decryptEntry, isDeviceStorableKeyId, type KeyBagEntry, type EncryptedKeyBagEntry,
} from '../crypto/keybag';
import { keys } from '../state/keys';
import { getSlotKey } from '../state/device-slots';
import { encryptAndStoreKey, getActiveCid, getStoredKeyEntries } from '../state/device-storage';
import {
    MANDATORY_DEVICE_KEY_IDS, allChosenDeviceKeyManifest, catalogueRowForKeyId,
    matchesDeviceKeyFamily, readDeviceKeyManifest, writeDeviceKeyManifest,
} from '../state/device-key-selection';
import { promptAndDeriveKey } from './supersecret-prompt';

/** What the "keep this key on this device?" question is asked about. */
export interface KeepKeyOnDeviceQuestion {
    /** The bag key id being offered — the exact id, agent keys included. */
    keyId: string;
    /** The catalogue label, or the raw id when no row knows it. */
    label: string;
    /** The caller's one phrase for what the key was just used for. */
    purpose: string;
}

export interface RequireKeyOptions {
    /** Opens the supersecret prompt and the import question. */
    shell: ShellAPI;
    /** One phrase naming what the key is needed for, shown to the member —
     *  "issue a chat certificate", "open a stored file". */
    purpose: string;
    /** `'ask'` offers to keep the key on this device once it is loaded;
     *  `'never'` must not, and is what a COPY hand-over passes. */
    import: 'ask' | 'never';
    /** The identity to import under. Defaults to the active one; when there is
     *  none, the key is still loaded for the session and nothing is offered. */
    cid?: string;
    /** Test seam for the import question (the default renders the modal). */
    confirmImport?: (input: KeepKeyOnDeviceQuestion) => Promise<boolean>;
}

/**
 * The shape a consumer's injected dep takes: the loader, already carrying the
 * shell, so a handler needs none. Wired in `key-grant/client.ts`; a handler
 * whose dep is absent answers `key-not-on-device` rather than prompting.
 *
 * `opts.import` defaults to `'ask'`. A COPY hand-over passes `'never'`: a
 * module asking for the BYTES of a key the member keeps off this device is no
 * reason to start storing it here (§9.2, `export-mail-encrypt`).
 */
export type RequireKeyFn = (
    keyId: string,
    purpose: string,
    opts?: { import?: 'ask' | 'never' },
) => Promise<KeyBagEntry[]>;

/**
 * What the supersecret prompt says (desktop-setup §6.1).
 *
 * The account HAS the key — every account is given `efs_encrypt` at
 * onboarding, and the same is true of every other loadable id here. What this
 * device does not have is a copy, because the member kept it off this device.
 * So the words are "kept off this device", never "missing", "not published" or
 * "not available": those describe a broken account and send the member hunting
 * for a fault that does not exist.
 *
 * Pure, so the wording is a test and not a screenshot.
 */
export function describeOnDemandLoad(
    label: string, purpose: string,
): { title: string; description: string } {
    const key = label.toLowerCase();
    return {
        title: `Load your ${key}`,
        description:
            `Your ${key} is kept off this device, and it is needed to ${purpose}. `
            + 'Enter your supersecret to unlock it from your account; the supersecret is '
            + 'used only to unlock this one key, then discarded.',
    };
}

/**
 * What the import offer says (desktop-setup §6.1).
 *
 * Three things the old copy left out: that leaving the key off is a standing
 * choice rather than an accident, what saying no costs (§0 "optional steps say
 * what skipping costs"), and where the choice lives afterwards, so the member
 * can change it without waiting to be asked again.
 */
export function describeKeepKeyOnDevice(label: string): {
    title: string; body: string; keepLabel: string; declineLabel: string;
} {
    const key = label.toLowerCase();
    return {
        title: `Keep your ${key} on this device?`,
        body:
            `Your ${key} is kept off this device, which is why you were asked for your `
            + 'supersecret. Keep it here and it is stored encrypted under this device\'s own '
            + 'key, and this device will not ask again. Leave it off and it is used only '
            + 'until you close this tab — the next time will ask for your supersecret again. '
            + 'Either way you can change your mind under Devices, "Keys on this device".',
        keepLabel: 'Keep it here',
        declineLabel: 'Leave it off this device',
    };
}

/**
 * Every generation of `keyId` this session can use, oldest first — for
 * chain-walking, which is why it is the whole chain and not the active entry.
 *
 * Empty means the key could not be loaded: the account holds no such key, or
 * the member dismissed the prompt. Callers answer `key-not-on-device`.
 *
 * @throws Error('cid-locked') when the key bag is locked — rule 1 above.
 * @throws Error when asked for a mandatory key id — rule 2 above.
 */
export async function requireKey(
    keyId: string,
    opts: RequireKeyOptions,
): Promise<KeyBagEntry[]> {
    if (MANDATORY_DEVICE_KEY_IDS.includes(keyId)) {
        throw new Error(
            `'${keyId}' is a mandatory device key and must never be loaded on demand — `
            + 'it is on every device that stores keys at all (device-setup-restructure '
            + '§1 item 10), and prompting for it would put a supersecret dialog in front '
            + 'of a non-interactive call');
    }

    const inMemory = chainInMemory(keyId);
    if (inMemory.length > 0) return inMemory;

    // Rule 1. Never a prompt for a tab that is merely locked.
    if (!keys.isLoaded()) throw new Error('cid-locked');

    const serverBag = await accountApi.getKeyBag();
    const wanted = serverBag.entries.filter(e => e.keyId === keyId) as EncryptedKeyBagEntry[];
    // Nothing to ask for: the account has never held this key. Asking for a
    // supersecret to decrypt zero entries is a dialog with no outcome.
    if (wanted.length === 0) return [];

    // Verified against a LIVE entry, never a revoked one — a revoked entry may
    // predate a re-derivation and would fail a correct supersecret.
    const verifyAgainst = serverBag.entries.find(e => e.status !== 'revoked')
        ?? serverBag.entries[0]
        ?? null;

    const label = catalogueRowForKeyId(keyId)?.label ?? keyId;
    const result = await promptAndDeriveKey(opts.shell, {
        saltB64: serverBag.keySalt,
        verifyAgainst: verifyAgainst as EncryptedKeyBagEntry | null,
        ...describeOnDemandLoad(label, opts.purpose),
    });
    if (!result) return [];

    const derivedKey = result.derivedKey;
    const loaded: KeyBagEntry[] = [];
    try {
        for (const entry of wanted) {
            const plain = await decryptEntry(entry, derivedKey);
            // Session use. `addEntry` replaces (keyId, keyIndex) rather than
            // duplicating, so a second load is idempotent.
            keys.addEntry(plain);
            loaded.push(plain);
        }
    } finally {
        // Rule 3: on the throwing path too.
        derivedKey.fill(0);
    }

    loaded.sort((a, b) => (a.keyIndex ?? 0) - (b.keyIndex ?? 0));

    if (opts.import === 'ask') await offerToKeepOnDevice(keyId, label, loaded, opts);
    return loaded;
}

/** The in-memory chain as KeyBagEntry rows (the bag's own, not copies of the
 *  secret bytes — `keys.getKeyChain` decodes, and a caller that zeroed those
 *  would blind the next reader). */
function chainInMemory(keyId: string): KeyBagEntry[] {
    const bag = keys.getKeyBag();
    if (!bag) return [];
    return bag.entries
        .filter(e => e.keyId === keyId)
        .sort((a, b) => (a.keyIndex ?? 0) - (b.keyIndex ?? 0));
}

/**
 * Ask whether to keep the freshly loaded key here, and act on a yes.
 *
 * Silent — no question at all — when there is nothing to act on: no identity,
 * no device encryption key in memory (a supersecret-only session, or "no local
 * storage"), a key that must never reach a device (`recovery_kit`), or a key
 * this device already stores. A question whose "yes" would change nothing is
 * worse than no question.
 */
async function offerToKeepOnDevice(
    keyId: string,
    label: string,
    loaded: readonly KeyBagEntry[],
    opts: RequireKeyOptions,
): Promise<void> {
    if (loaded.length === 0) return;
    if (!isDeviceStorableKeyId(keyId)) return;

    const cid = opts.cid ?? getActiveCid();
    if (!cid) return;

    const dek = getSlotKey(cid);
    if (!dek) return;

    if (getStoredKeyEntries(cid).some(e => e.keyId === keyId)) {
        // Already here: reconcile the manifest (the copy and the choice
        // disagreed) and ask nothing.
        chooseInManifest(cid, keyId);
        return;
    }

    const confirm = opts.confirmImport ?? ((input) => askKeepOnDevice(opts.shell, input));
    const keep = await confirm({ keyId, label, purpose: opts.purpose });
    if (!keep) return;

    for (const entry of loaded) {
        await encryptAndStoreKey(cid, entry.keyId, aes.fromBase64(entry.privateKey), dek, {
            algorithm: entry.algorithm,
            keyIndex: entry.keyIndex ?? 0,
            status: entry.status ?? 'active',
        });
    }
    chooseInManifest(cid, keyId);
}

/**
 * Record the decision the import just made: this device holds `keyId`.
 *
 * Under the row that GOVERNS the key, not the raw id — the agent rows are one
 * family toggle, and leaving the family declined while its member key sits
 * encrypted on the device would show the member a picker that disagrees with
 * their own disk. An absent manifest starts from "everything chosen" (§7.1),
 * so an old device does not acquire a pile of freshly "new" rows.
 */
function chooseInManifest(cid: string, keyId: string): void {
    const row = catalogueRowForKeyId(keyId);
    const governing = row?.family ?? row?.keyId ?? keyId;
    const manifest = readDeviceKeyManifest(cid) ?? allChosenDeviceKeyManifest();
    writeDeviceKeyManifest(cid, {
        v: 1,
        chosen: [...manifest.chosen, governing],
        declined: manifest.declined.filter(
            id => id !== governing && id !== keyId
                && !(id.includes('*') && matchesDeviceKeyFamily(id, keyId))),
    });
}

/**
 * The import question. Minimal on purpose: the consent that matters was the
 * supersecret the member just typed, and this asks only whether the answer
 * should outlive the session.
 */
function askKeepOnDevice(
    shell: ShellAPI,
    input: KeepKeyOnDeviceQuestion,
): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
        let resolved = false;
        const resolveOnce = (keep: boolean): void => {
            if (resolved) return;
            resolved = true;
            handle.close();
            resolve(keep);
        };

        // `cid-pane-content` for the same reason `supersecret-prompt.ts` uses
        // it: only that root shrinks to the modal card without clipping.
        const copy = describeKeepKeyOnDevice(input.label);
        const card = createElement('div', {
            className: 'cid-pane-content',
            style: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px' },
        });
        card.appendChild(createElement('h2', {
            className: 'cs-modal-title',
            textContent: copy.title,
            style: { margin: '0', fontSize: '18px' },
        }));
        card.appendChild(createElement('p', {
            className: 'cid-auth-subtitle',
            textContent: copy.body,
            style: { fontSize: '13px', opacity: '0.85', margin: '0' },
        }));

        const footer = createElement('div', {
            style: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
        });
        const noBtn = createElement('button', {
            className: 'cs-btn cs-btn--default',
            textContent: copy.declineLabel,
        });
        noBtn.addEventListener('click', () => resolveOnce(false));
        footer.appendChild(noBtn);
        const yesBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes',
            textContent: copy.keepLabel,
        });
        yesBtn.addEventListener('click', () => resolveOnce(true));
        footer.appendChild(yesBtn);
        card.appendChild(footer);

        const handle = shell.showModal(card, {
            size: 'normal',
            // Dismissing is a "no": the key stays in this session either way.
            onClose: () => resolveOnce(false),
        });
    });
}
